import { config } from '@/configs'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Tabs } from '@/shared/ui/tabs'

import { type FC, useEffect, useState } from 'react'

import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { APIErrorSimulator } from '../errorSimulator/APIErrorSimulator'
import { useAPIErrorSimulatorEffect } from '../errorSimulator/useAPIErrorSimulator'
import { FeatureToggle } from '../featureToggle/FeatureToggle'
import { LanguageSelectorPanel } from '../languageSelector/LanguageSelectorPanel'
import { PermissionPanel } from '../permissionPanel/PermissionPanel'

/**
 * TestingPanel - Floating dev tooling panel.
 *
 * Renders a floating button in the bottom-left corner of the screen.
 * Clicking it opens a Dialog with tabs for testing utilities:
 * - **Error Simulator**: Intercept API requests and simulate HTTP errors
 * - **Feature Toggles**: Enable/disable registered feature flags
 * - **Language**: Switch the UI language (including Dev Mode for translation keys)
 * - **Permissions**: Toggle user permissions and inspect page-level permission declarations
 *
 * Only rendered when `config.testingTooling` is `true` (local and dev environments only).
 *
 * @example
 * // In App.tsx or root layout:
 * ```tsx
 * <TestingPanel />
 * ```
 */
export const TestingPanel: FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isHidden, setIsHidden] = useState(false)

  if (!config.testingTooling) {
    return null
  }

  // This is ok to call the hook conditionally because it comes from a constant
  // that doesn't change during the app lifecycle.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useAPIErrorSimulatorEffect()

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    // when the panel is opened, add a class to the body to disable
    // pointer events. We override it to allow interaction with the
    // React Query Devtools
    const timer = setTimeout(() => {
      document.body.style = ''
    }, 100)
    return () => clearTimeout(timer)
  }, [isOpen])

  return (
    <>
      {!isHidden && (
        <div className="fixed bottom-4 left-1/2 z-50 flex items-center gap-1">
          <Button
            label="🛠️"
            intent="neutral"
            variant="outline"
            size="sm"
            onClick={() => setIsOpen(true)}
            aria-label="Open testing panel"
          />
          <Button
            label="✕"
            intent="neutral"
            variant="ghost"
            size="sm"
            onClick={() => setIsHidden(true)}
            aria-label="Hide testing panel"
          />
        </div>
      )}

      {isOpen && <ReactQueryDevtools buttonPosition="bottom-right" initialIsOpen={false} />}

      <Dialog isOpen={isOpen} onOpenChange={setIsOpen} size="md">
        <Dialog.Header title="🛠️ Testing Tools" />
        <Dialog.Body>
          <Tabs defaultValue="language" variant="border" size="sm">
            <Tabs.Cells>
              <Tabs.Cell value="language">
                <Tabs.TextCell>Language</Tabs.TextCell>
              </Tabs.Cell>
              <Tabs.Cell value="featureToggles">
                <Tabs.TextCell>Feature Toggles</Tabs.TextCell>
              </Tabs.Cell>
              <Tabs.Cell value="errorSimulator">
                <Tabs.TextCell>Error Simulator</Tabs.TextCell>
              </Tabs.Cell>
              <Tabs.Cell value="permissions">
                <Tabs.TextCell>Permissions</Tabs.TextCell>
              </Tabs.Cell>
            </Tabs.Cells>

            <Tabs.Content value="errorSimulator">
              <div className="p-4">
                <APIErrorSimulator />
              </div>
            </Tabs.Content>

            <Tabs.Content value="featureToggles">
              <div className="p-4">
                <FeatureToggle />
              </div>
            </Tabs.Content>

            <Tabs.Content value="language">
              <div className="p-4">
                <LanguageSelectorPanel />
              </div>
            </Tabs.Content>

            <Tabs.Content value="permissions">
              <div className="p-4">
                <PermissionPanel />
              </div>
            </Tabs.Content>
          </Tabs>
        </Dialog.Body>
      </Dialog>
    </>
  )
}
